import type {Route, Routes} from './config';
import type {LDClientImpl} from '@launchdarkly/js-server-sdk-common';

type LDClient = Pick<LDClientImpl, 'variation'>;

// Various sentry SDK's might be used and passed into the router, each with
// potentially their own types. This specifies the minimum interface that
// eaach should support.
type SentryPartial = {
  captureException(exception: any, hint?: any): void;
};

/**
 * ErrNoDefaultRoute is thrown if no default route is provided. A
 * route is considered the default if it's pattern is `"*"`
 */
export const ErrNoDefaultRoute = new Error('No default/fallback route provided');

/**
 * Router contains the routing table and will decide which origin
 * a request should be routed to
 */
export class Router<Origin extends string> {
  defaultRoute: Route<Origin>;
  routes: Routes<Origin>;
  ldClient: LDClient | undefined;

  /**
   * @param routes - A list of routes that it should evaluate requests against
   * @param ldClient  - Launch darkly client for feature flag access
   */
  constructor(routes: Routes<Origin>, ldClient?: LDClient | undefined) {
    // doing a bit of type hackery, need to guarantee it's set
    // before leaving constructor
    this.defaultRoute = undefined as unknown as Route<Origin>;
    for (const route of routes) {
      if (route.pattern === '*') {
        this.defaultRoute = route;
      }
    }

    // guarantees defaultRoute is set or throws a fit
    if (!this.defaultRoute) {
      throw ErrNoDefaultRoute;
    }
    this.routes = routes;
    this.ldClient = ldClient;
  }

  /**
   * match returns the origin where `pathname` should be routed to
   * @param pathname - Pathname of the request URL
   * @returns The origin name it should route to
   */
  public async match(
    sentry: SentryPartial,
    pathname: string,
    context: {key: string} = {key: 'anonymous'}
  ): Promise<Origin> {
    for (const route of this.routes) {
      const isMatch = matchRoute(route.pattern, pathname);
      if (!isMatch) continue;
      console.log(`${pathname} is a match with ${JSON.stringify(route)}`);

      if (route.flag) {
        console.log(`route ${route.pattern} has flag, checking...`);
        if (this.ldClient) {
          try {
            const isEnabled = await this.ldClient.variation(route.flag, context, false);
            console.log(`route ${route.pattern} flag evaluates to ${isEnabled}`);
            if (isEnabled === true) {
              return route.origin;
            } else {
              continue;
            }
          } catch (err) {
            console.log('failed to check launchdarkly flag', err);
            sentry.captureException(err);
            continue;
          }
        } else {
          console.log(
            'launchdarkly unavailable, unable to check feature flag. Continuing to evaluate routes'
          );
          // if we can't check the flag, we fall back to false
          continue;
        }
      }

      return route.origin;
    }

    return this.defaultRoute.origin;
  }
}

/**
 * Check if pathname is a match against pattern
 * @param pattern - A URL pathname pattern, may contain wildcard (`*`) at the end
 * @param pathname  - URL pathname to check for match
 * @returns true if the pathname matches with the pattern
 *
 * @internal
 */
function matchRoute(pattern: string, pathname: string): boolean {
  if (pattern.endsWith('*')) {
    return pathname.startsWith(pattern.slice(0, -1));
  }
  return pathname === pattern;
}
